import * as React from "react";
import { useRef, useState, useCallback, useEffect } from "react";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";

/**
 * DepthCard — large 3D parallax card with cursor-tracked rotation, optional
 * multi-layer depth, optional cursor-following spotlight, and a staggered
 * text-reveal overlay that slides title + description up on hover.
 *
 *   ┌─ card · perspective · notch silhouette ─────────╲┐
 *   │  ┌─ image layers (parallax tilt + scale 1.3) ─╲┐ │
 *   │  │                                            │ │
 *   │  │ (spotlight overlay tracks cursor)          │ │
 *   │  │                                            │ │
 *   │  ├─ dark gradient veil (bottom 2/3) ──────────┤ │
 *   │  │  Title  (slide-up + fade)                  │ │
 *   │  │  Description  (slide-up + fade · 75ms)     │ │
 *   │  └────────────────────────────────────────────┘ │
 *   └─────────────────────────────────────────────────┘
 *
 * The surface wears the Onyx hex-notch silhouette (`nx-s`, the card scale).
 * The two details a `clip-path` would break in the v1 way are handled per the
 * surface recipe (issue #158), and they survive the 3D transform too:
 *   - ELEVATION is a clip-safe `.elev-card` `drop-shadow` (lifting to
 *     `.elev-panel` on hover), NOT an outer `box-shadow` — a box-shadow is both
 *     clipped away by the notch clip-path and dropped by the parallax transform.
 *   - The OUTLINE is a continuous `nx-rim` RING overlay that traces the full
 *     notched silhouette including the chamfer, NOT a real CSS `border` (sliced
 *     at the notch diagonal).
 *
 * Adapted from the ReactBits Pro "depth-card-tw" recipe. Pretty's adaptations:
 *
 * - **Anatomy**: forwardRef + displayName, named export, relative `cn` import.
 * - **Surface**: Onyx `nx-s` notch + `.elev-*` drop-shadow + `nx-rim` keyline.
 *   The silhouette is now the notch clip, so the rim ring + drop-shadow follow
 *   the chamfer rather than a plain rounded rect. The `borderRadius` prop is
 *   retained for back-compat as the no-clip fallback radius (used on browsers
 *   without `shape()` clip-path support, where `nx-s` degrades to a rounded
 *   rect); it no longer drives the notched silhouette.
 * - **Tokens**: dark gradient veil uses `from-black/85 via-black/45` to match
 *   the canonical image-overlay convention shared with `StudioCard`,
 *   `ExperienceCard`, and `CollectionCard`. The overlay text stays `text-white`
 *   / `text-white/85` because the gradient is theme-invariant by design (text
 *   sits over arbitrary images, not over surface tokens).
 * - **Reduced motion**: when `prefers-reduced-motion: reduce` is set, all 3D
 *   tilt, parallax, and spotlight effects are disabled; the text overlay
 *   collapses to a straight opacity fade (no Y translate).
 * - **Reveal animation**: `slide` (default), `fade`, `scale`, or `none`. The
 *   stagger between title and description is the `staggerDelay` prop (ms).
 *
 * The 3D tilt + parallax layers + cursor-follow spotlight are the load-bearing
 * value of this primitive. Reach for `DepthCard` when the visual moment is
 * worth a heavy primitive (hero modules, splash sections, depth-treated studio
 * covers); use `StudioCard` for general studio-card surfaces that need the
 * full chrome of cover + content + footer + dropdown.
 */

interface DepthLayer {
  /** Image URL for this layer. */
  image: string;
  /** Depth multiplier for parallax effect (higher = more movement). */
  depth: number;
}

export interface DepthCardProps {
  /** Background image URL for the card. */
  image?: string;
  /** Card title — always rendered on top of the gradient veil. */
  title: string;
  /** Optional card description — reveals beneath the title on hover. */
  description?: string;
  /** Width of the card in pixels. Default 480 (paired with `height=270` for a 16:9 cover; pretty#167). */
  width?: number;
  /** Height of the card in pixels. Default 270 (16:9 with the default `width=480`). */
  height?: number;
  /** Maximum rotation angle in degrees. */
  maxRotation?: number;
  /** Maximum translation for parallax effect in pixels. */
  maxTranslation?: number;
  /** Border radius of the card (CSS length). */
  borderRadius?: string;
  /** Additional CSS classes for the wrapper. */
  className?: string;
  /** Additional CSS classes for the inner card content. */
  contentClassName?: string;
  /** Callback when card is clicked. */
  onClick?: () => void;
  /** URL to navigate to when card is clicked. Renders the wrapper as `<a>`. */
  href?: string;
  /** Target for link navigation. */
  target?: "_blank" | "_self" | "_parent" | "_top";
  /** Alt text for the background image. */
  imageAlt?: string;
  /** Disable 3D effects on mobile devices (UA + width < 768). */
  disableOnMobile?: boolean;
  /** ARIA label for accessibility. */
  ariaLabel?: string;
  /** Multiple layers for advanced parallax effect. Overrides `image`. */
  layers?: DepthLayer[];
  /** Stagger delay between title and description reveals, in ms. */
  staggerDelay?: number;
  /** Content reveal animation type. */
  revealAnimation?: "slide" | "fade" | "scale" | "none";
  /** Respect `prefers-reduced-motion`. Defaults to true. */
  respectReducedMotion?: boolean;
  /** Enable cursor-following spotlight overlay. Defaults to true. */
  spotlight?: boolean;
  /** Spotlight color (any CSS color). */
  spotlightColor?: string;
  /**
   * Optional content rendered inside the same tilt + perspective + rounded
   * silhouette as the cover, immediately below the image region. Use when
   * a card needs a command surface (e.g. `<CardCommandBar />`) that should
   * feel like one coherent card with the cover, not a separate strip glued
   * underneath. With a footer set, the outer height becomes auto (image
   * height + footer's natural height); without one, behaviour is unchanged
   * and the card is exactly `height` tall. pretty#168.
   */
  footer?: React.ReactNode;
}

export const DepthCard = React.forwardRef<HTMLDivElement, DepthCardProps>(
  (
    {
      image,
      title,
      description,
      // 16:9 by default — studio covers and most other AISquare cover
      // surfaces are wide, not portrait. The portrait default (240x320)
      // the React Bits recipe shipped with looked great for poster
      // mockups but produced an awkward narrow card when consumers fed
      // in a 16:9 cover (image cropped or letterboxed). 480x270 keeps
      // the same width as the previous max while widening the canvas,
      // and matches the cover-aspect contract `StudioCard` documents
      // via `STUDIO_CARD_COVER_ASPECT`. Consumers who still want a
      // portrait splash card pass explicit `width` / `height` props.
      width = 480,
      height = 270,
      maxRotation = 20,
      maxTranslation = 20,
      borderRadius = "16px",
      className,
      contentClassName,
      onClick,
      href,
      target = "_self",
      imageAlt,
      disableOnMobile = false,
      ariaLabel,
      layers,
      staggerDelay = 75,
      revealAnimation = "slide",
      respectReducedMotion = true,
      spotlight = true,
      spotlightColor = "rgba(255, 255, 255, 0.5)",
      footer,
    },
    forwardedRef,
  ) => {
    const hasFooter = footer !== undefined && footer !== null && footer !== false;
    const cardRef = useRef<HTMLDivElement>(null);
    React.useImperativeHandle(forwardedRef, () => cardRef.current as HTMLDivElement);

    const innerRef = useRef<HTMLDivElement>(null);
    const layerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const spotlightRef = useRef<HTMLDivElement>(null);

    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    const targetRef = useRef({ x: 0, y: 0, rotateX: 0, rotateY: 0 });
    const currentRef = useRef({ x: 0, y: 0, rotateX: 0, rotateY: 0 });
    const rafId = useRef<number | undefined>(undefined);

    useEffect(() => {
      if (!disableOnMobile) return;
      const checkMobile = () => {
        setIsMobile(
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          ) || window.innerWidth < 768,
        );
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, [disableOnMobile]);

    useEffect(() => {
      if (!respectReducedMotion) return;
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
      handler({ matches: mediaQuery.matches } as MediaQueryListEvent);
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }, [respectReducedMotion]);

    const shouldDisableEffects = (disableOnMobile && isMobile) || prefersReducedMotion;

    useEffect(() => {
      if (shouldDisableEffects) return;

      const lerp = (start: number, end: number, factor: number) => start + (end - start) * factor;

      const animate = () => {
        const target = targetRef.current;
        const current = currentRef.current;

        current.x = lerp(current.x, target.x, 0.1);
        current.y = lerp(current.y, target.y, 0.1);
        current.rotateX = lerp(current.rotateX, target.rotateX, 0.1);
        current.rotateY = lerp(current.rotateY, target.rotateY, 0.1);

        if (innerRef.current) {
          innerRef.current.style.transform = `rotateX(${current.rotateX}deg) rotateY(${current.rotateY}deg)`;
        }

        if (layers && layers.length > 0) {
          layerRefs.current.forEach((layer, index) => {
            if (layer) {
              const depth = layers[index].depth;
              layer.style.transform = `translateX(${current.x * depth}px) translateY(${current.y * depth}px) scale(1.3)`;
            }
          });
        } else if (layerRefs.current[0]) {
          layerRefs.current[0].style.transform = `translateX(${current.x}px) translateY(${current.y}px) scale(1.3)`;
        }

        rafId.current = requestAnimationFrame(animate);
      };

      rafId.current = requestAnimationFrame(animate);

      return () => {
        if (rafId.current) cancelAnimationFrame(rafId.current);
      };
    }, [shouldDisableEffects, layers]);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
        if (!cardRef.current || shouldDisableEffects) return;

        const rect = cardRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const mouseX = e.clientX - centerX;
        const mouseY = e.clientY - centerY;

        if (spotlight && spotlightRef.current) {
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          spotlightRef.current.style.background = `radial-gradient(600px circle at ${x}px ${y}px, ${spotlightColor} 0%, rgba(255, 255, 255, 0.1) 40%, transparent 100%)`;
        }

        const percentX = mouseX / (rect.width / 2);
        const percentY = mouseY / (rect.height / 2);

        targetRef.current = {
          x: percentX * -maxTranslation,
          y: percentY * -maxTranslation,
          rotateX: percentY * -maxRotation,
          rotateY: percentX * maxRotation,
        };
      },
      [maxRotation, maxTranslation, shouldDisableEffects, spotlight, spotlightColor],
    );

    const handleMouseEnter = useCallback(() => {
      setIsHovered(true);
      if (spotlightRef.current) {
        spotlightRef.current.style.opacity = "1";
      }
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
      targetRef.current = { x: 0, y: 0, rotateX: 0, rotateY: 0 };
      if (spotlightRef.current) {
        spotlightRef.current.style.opacity = "0";
      }
    }, []);

    const handleClick = useCallback(() => {
      if (href) {
        if (target === "_blank") {
          window.open(href, target, "noopener,noreferrer");
        } else {
          window.location.href = href;
        }
      }
      onClick?.();
    }, [href, target, onClick]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement | HTMLAnchorElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      },
      [handleClick],
    );

    const CardWrapper = href ? "a" : "div";
    const wrapperProps = href
      ? {
          href,
          target,
          rel: target === "_blank" ? "noopener noreferrer" : undefined,
        }
      : {};

    const buildRevealClasses = (delay: number, descriptionTier: boolean) => {
      if (revealAnimation === "none") return "";
      const base = "transform transition-all duration-500";
      const inactiveByMode: Record<"slide" | "fade" | "scale", string> = {
        slide: descriptionTier ? "translate-y-4 opacity-0" : "translate-y-4 opacity-90",
        fade: descriptionTier ? "opacity-0" : "opacity-90",
        scale: descriptionTier ? "scale-95 opacity-0" : "scale-95 opacity-90",
      };
      const active = "translate-y-0 opacity-100 scale-100";
      const delayClass = delay > 0 ? `delay-[${delay}ms]` : "";
      return cn(base, isHovered ? active : inactiveByMode[revealAnimation], delayClass);
    };

    return (
      <CardWrapper
        {...wrapperProps}
        ref={cardRef as React.RefObject<HTMLDivElement & HTMLAnchorElement>}
        className={cn(
          "relative cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          className,
        )}
        style={{
          width: `${width}px`,
          ...(hasFooter ? {} : { height: `${height}px` }),
          perspective: "1000px",
          // No `overflow: hidden` / wrapper radius here: the silhouette + image
          // clipping live on the inner element (the `nx-s` notch clip-path), and
          // clipping the wrapper would slice the inner element's `.elev-*`
          // drop-shadow (which renders OUTSIDE the box).
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClick && !href ? handleClick : undefined}
        onKeyDown={onClick || href ? handleKeyDown : undefined}
        role={onClick || href ? "button" : undefined}
        tabIndex={onClick || href ? 0 : undefined}
        aria-label={ariaLabel || `${title} card`}
      >
        <div
          ref={innerRef}
          className={cn(
            // Onyx surface: the `nx-s` notch clip-path shapes the silhouette
            // (and clips the parallax cover to it), the `.elev-*` drop-shadow
            // is clip-safe + transform-safe and deepens on hover, replacing the
            // v1 `box-shadow` (which the clip + the transform would drop).
            nx.s,
            "relative w-full bg-card",
            !hasFooter && "h-full",
            "transition-[filter] duration-300 ease-out",
            isHovered ? "elev-panel" : "elev-card",
            contentClassName,
          )}
          style={{
            // Fallback radius for the no-`shape()` path (where `nx-s` degrades
            // to a rounded rect). Under the notch clip-path it has no visible
            // effect — the clip defines the silhouette.
            borderRadius,
            transformStyle: "preserve-3d",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            willChange: "transform",
          }}
        >
          <div className="relative w-full overflow-hidden" style={{ height: `${height}px` }}>
            <div
              className="absolute inset-0 overflow-hidden"
              aria-label={imageAlt}
              role={imageAlt ? "img" : undefined}
            >
              {layers && layers.length > 0 ? (
                layers.map((layer, index) => (
                  <div
                    key={index}
                    ref={(el) => {
                      layerRefs.current[index] = el;
                    }}
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
                    style={{
                      backgroundImage: `url(${layer.image})`,
                      zIndex: index,
                      opacity: index === 0 ? 1 : 0.6,
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                    }}
                  />
                ))
              ) : (
                <div
                  ref={(el) => {
                    layerRefs.current[0] = el;
                  }}
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
                  style={{
                    backgroundImage: image ? `url(${image})` : undefined,
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                />
              )}
            </div>

            {spotlight && (
              <div
                ref={spotlightRef}
                className="absolute inset-0 pointer-events-none z-20 transition-opacity duration-300 ease-out opacity-0"
                style={{
                  mixBlendMode: "soft-light",
                  filter: "blur(2px)",
                }}
                aria-hidden
              />
            )}

            <div className="absolute inset-0 z-30 flex flex-col justify-end p-6 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
              <h3
                className={cn("text-2xl font-bold text-white mb-2", buildRevealClasses(0, false))}
              >
                {title}
              </h3>
              {description && (
                <p className={cn("text-sm text-white/85", buildRevealClasses(staggerDelay, true))}>
                  {description}
                </p>
              )}
            </div>
          </div>

          {hasFooter ? <div className="relative bg-card">{footer}</div> : null}

          {/* Continuous keyline — a 1px `nx-rim` ring clipped to the notched
              silhouette, NOT a CSS border (which the notch clip-path would
              slice at the chamfer). Sits above the cover + footer, traces the
              full silhouette including the notch. */}
          <span
            aria-hidden
            className="nx-rim pointer-events-none absolute inset-0 z-40 [--nx-rim:hsl(var(--border))]"
            style={{ clipPath: NX_RING_CLIP.s }}
          />
        </div>
      </CardWrapper>
    );
  },
);
DepthCard.displayName = "DepthCard";
